import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Sparkles,
  MousePointer,
  Target,
  Rocket,
  Crown,
} from 'lucide-react'
import Slide, { StaggerContainer, StaggerItem } from '../components/Slide'

interface SlideProps {
  isActive: boolean
}

const skillJourney = [
  {
    icon: MousePointer,
    label: 'Basic',
    multiplier: '1x',
    color: 'bg-white/10',
    activeColor: 'bg-white/20',
    borderColor: 'border-white/30',
    textColor: 'text-white/60',
    gradientColor: 'from-white/20 to-white/5',
    skills: [
      { text: 'Copy-paste prompts from web', example: '"write a function to sort array"' },
      { text: 'Accept first AI response', example: 'No iteration or refinement' },
      { text: 'Basic autocomplete usage', example: 'Tab-complete suggestions only' },
    ],
    outcome: 'Marginal time savings, frequent rework needed',
  },
  {
    icon: Target,
    label: 'Structured',
    multiplier: '2x',
    color: 'bg-electric-cyan/10',
    activeColor: 'bg-electric-cyan/20',
    borderColor: 'border-electric-cyan/50',
    textColor: 'text-electric-cyan',
    gradientColor: 'from-electric-cyan/20 to-electric-cyan/5',
    skills: [
      { text: 'Provide clear context & constraints', example: '"Using TypeScript, React 18, no external libs..."' },
      { text: 'Specify expected output format', example: '"Return JSON with {id, name, status} fields"' },
      { text: 'Include relevant code snippets', example: 'Paste existing interfaces & types' },
    ],
    outcome: 'Consistent results, less back-and-forth',
  },
  {
    icon: Rocket,
    label: 'Advanced',
    multiplier: '3x',
    color: 'bg-electric-purple/10',
    activeColor: 'bg-electric-purple/20',
    borderColor: 'border-electric-purple/50',
    textColor: 'text-electric-purple',
    gradientColor: 'from-electric-purple/20 to-electric-purple/5',
    skills: [
      { text: 'Chain-of-thought prompting', example: '"Think step by step: 1) analyze, 2) plan, 3) implement"' },
      { text: 'Iterative refinement cycles', example: '"Good, now add error handling and tests"' },
      { text: 'Role-based instructions', example: '"Act as a senior security engineer..."' },
    ],
    outcome: 'Production-ready code, complex problem solving',
  },
  {
    icon: Crown,
    label: 'Expert',
    multiplier: '5x',
    color: 'bg-electric-orange/10',
    activeColor: 'bg-electric-orange/20',
    borderColor: 'border-electric-orange/50',
    textColor: 'text-electric-orange',
    gradientColor: 'from-electric-orange/20 to-electric-orange/5',
    skills: [
      { text: 'Multi-step AI workflows', example: 'Plan → Implement → Review → Test pipeline' },
      { text: 'System architecture with AI', example: 'Design docs + diagrams + code generation' },
      { text: 'Custom prompt frameworks', example: 'CLAUDE.md, project-specific templates' },
    ],
    outcome: 'AI as force multiplier, strategic advantage',
  },
]

export default function WhyLearnPromptingSlide({ isActive: _isActive }: SlideProps) {
  const [selectedStage, setSelectedStage] = useState(0) // Start with Basic selected

  return (
    <Slide>
      <StaggerContainer className="h-full flex flex-col justify-center">
        {/* Header */}
        <StaggerItem>
          <div className="mb-8 text-center">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-3">
              Your <span className="text-gradient">Skill Journey</span>
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              The path from basic AI user to prompt expert — click each level to explore
            </p>
          </div>
        </StaggerItem>

        {/* Skill Journey - Main Focus */}
        <StaggerItem className="flex-1 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="max-w-5xl mx-auto w-full"
          >
            {/* Journey timeline */}
            <div className="relative mb-8">
              {/* Progress line */}
              <div className="absolute top-10 left-16 right-16 h-1.5 bg-white/10 rounded-full">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(selectedStage / 3) * 100}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  className="h-full bg-gradient-to-r from-white/40 via-electric-cyan via-electric-purple to-electric-orange rounded-full"
                />
              </div>

              {/* Journey stages */}
              <div className="relative flex justify-between px-4">
                {skillJourney.map((stage, index) => {
                  const Icon = stage.icon
                  const isSelected = selectedStage === index
                  const isPast = index <= selectedStage
                  return (
                    <motion.button
                      key={stage.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1 }}
                      onClick={() => setSelectedStage(index)}
                      className="flex flex-col items-center cursor-pointer group"
                    >
                      <motion.div
                        whileHover={{ scale: 1.1, y: -4 }}
                        whileTap={{ scale: 0.95 }}
                        className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-3 border-2 transition-all duration-300 shadow-lg ${
                          isSelected
                            ? `${stage.activeColor} ${stage.borderColor} ring-4 ring-white/20`
                            : isPast
                              ? `${stage.color} ${stage.borderColor}`
                              : 'bg-white/5 border-white/10'
                        }`}
                      >
                        <Icon className={`w-8 h-8 ${isSelected ? stage.textColor : isPast ? 'text-white' : 'text-white/30'}`} />
                      </motion.div>
                      <span className={`text-base font-semibold mb-1 transition-colors ${isSelected ? stage.textColor : 'text-white/60'}`}>
                        {stage.label}
                      </span>
                      <motion.span
                        animate={isSelected ? { scale: [1, 1.1, 1] } : {}}
                        transition={{ duration: 0.5 }}
                        className={`text-3xl font-bold ${isSelected ? stage.textColor : 'text-white/20'}`}
                      >
                        {stage.multiplier}
                      </motion.span>
                    </motion.button>
                  )
                })}
              </div>
            </div>

            {/* Selected stage details - Larger panel */}
            <motion.div
              key={selectedStage}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className={`glass rounded-2xl p-6 min-h-[220px] bg-gradient-to-br ${skillJourney[selectedStage].gradientColor} border ${skillJourney[selectedStage].borderColor}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Skills needed */}
                <div>
                  <h5 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${skillJourney[selectedStage].textColor}`}>
                    <span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                      {(() => {
                        const Icon = skillJourney[selectedStage].icon
                        return <Icon className="w-4 h-4" />
                      })()}
                    </span>
                    What you need at {skillJourney[selectedStage].label} level
                  </h5>
                  <ul className="space-y-3">
                    {skillJourney[selectedStage].skills.map((skill, i) => (
                      <motion.li
                        key={`${selectedStage}-${i}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.05 + i * 0.05 }}
                        className="flex items-start gap-3"
                      >
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${skillJourney[selectedStage].textColor.replace('text-', 'bg-')}`} />
                        <div>
                          <span className="text-base text-white/80">{skill.text}</span>
                          <p className="text-sm text-white/40 font-mono mt-0.5">{skill.example}</p>
                        </div>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                {/* Outcome */}
                <div className="flex flex-col justify-center">
                  <h5 className={`text-lg font-semibold mb-4 ${skillJourney[selectedStage].textColor}`}>
                    What you get
                  </h5>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.15 }}
                    className="glass rounded-xl p-4"
                  >
                    <p className="text-xl text-white font-medium leading-relaxed">
                      {skillJourney[selectedStage].outcome}
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <span className={`text-3xl font-bold ${skillJourney[selectedStage].textColor}`}>
                        {skillJourney[selectedStage].multiplier}
                      </span>
                      <span className="text-white/50 text-sm">productivity multiplier</span>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </StaggerItem>

        {/* Bottom insight */}
        <StaggerItem>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-8 mb-[100px]"
          >
            <div className="glass rounded-xl p-4 max-w-3xl mx-auto">
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-electric-purple flex-shrink-0" />
                <p className="text-base text-white/70">
                  <span className="text-electric-cyan font-semibold">The reality:</span>{' '}
                  Most developers stay at Basic level. Moving to <span className="text-electric-purple font-semibold">Structured</span> alone
                  doubles your output. The journey to Expert is what separates good developers from great ones.
                </p>
              </div>
            </div>
          </motion.div>
        </StaggerItem>
      </StaggerContainer>
    </Slide>
  )
}
